/**
 * Mirrors packages/amgsicbo_v2/lib/domain/entities/bet.dart `BetCollection`.
 *
 * Keyed by numeric betItemId since SicBo has 54 distinct bet IDs and a
 * discriminated-string union doesn't fit cleanly.
 */
import type { BetType } from '../types/game';
import { betTypeById } from '../types/game';

export interface BetEntry {
  bet: BetType;
  amount: number;
  chips: number[];
}

export class BetCollection {
  private entries: Map<number, BetEntry> = new Map();

  static empty(): BetCollection {
    return new BetCollection();
  }

  copy(): BetCollection {
    const c = new BetCollection();
    for (const [k, v] of this.entries) {
      c.entries.set(k, { bet: v.bet, amount: v.amount, chips: [...v.chips] });
    }
    return c;
  }

  add(bet: BetType, amount: number): void {
    const cur = this.entries.get(bet.id);
    if (cur) {
      cur.amount += amount;
      cur.chips.push(amount);
    } else {
      this.entries.set(bet.id, { bet, amount, chips: [amount] });
    }
  }

  clear(): void {
    this.entries.clear();
  }

  clearBet(id: number): void {
    this.entries.delete(id);
  }

  amountFor(id: number): number {
    return this.entries.get(id)?.amount ?? 0;
  }

  get total(): number {
    let n = 0;
    for (const v of this.entries.values()) n += v.amount;
    return n;
  }

  get isEmpty(): boolean {
    return this.entries.size === 0;
  }

  get bets(): readonly BetEntry[] {
    return Array.from(this.entries.values());
  }

  /**
   * Convert to /action/bet `betDetail` lines.
   * Mirrors BetCollection.toApiFormat() — field is `amount` (NOT betAmount).
   */
  toBetLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      out.push({ betItemId: v.bet.id, amount: v.amount });
    }
    return out;
  }

  /**
   * Multiplayer-only: one line per INDIVIDUAL chip (not the per-bet total).
   *
   * The SignalR hub validates each PlaceBet `amount` against the configured
   * chip denominations and rejects a summed total (e.g. 100+100+100 → 300)
   * with "Invalid Chip Amount". So multiplayer submits one PlaceBet per chip,
   * each a valid denomination, instead of the batched total from
   * toBetLines(). Same betItemId derivation (`v.bet.id`) and same skip logic
   * as toBetLines(); the only difference is iterating `v.chips`. Invariant:
   * for each betItemId, the sum of toChipLines amounts == the toBetLines
   * amount.
   */
  toChipLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      for (const chip of v.chips) {
        if (chip <= 0) continue;
        out.push({ betItemId: v.bet.id, amount: chip });
      }
    }
    return out;
  }

  /** "Small:1000, Big:500" — for log entries. */
  toDetailsString(): string {
    const parts: string[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      parts.push(`${v.bet.name}:${Math.round(v.amount).toLocaleString('en-US')}`);
    }
    return parts.join(', ');
  }
}

export { betTypeById };
